import { useRef } from 'react';

export interface Timers {
  // [startTime, endTime, durationInMs]
  [key: string]: [number | null, number | null, number | null];
}

const useDuration = () => {
  const durationRef = useRef<Timers>({});

  /**
   * Starts the timer by setting starting timestamp.
   * If no key is provided DEFAULT timer will run.
   * @param key timer name
   * @param resume if true, it will resume the timer from the last paused time.
   */
  const startTimer = (
    key: string = 'DEFAULT',
    resume: boolean = false
  ): void => {
    if (resume && durationRef.current[key]?.length === 3) {
      const [startTime, endTime, duration] = durationRef.current[key];
      const newStartTime = Date.now() - ((endTime ?? 0) - (startTime ?? 0));
      durationRef.current[key] = [newStartTime, null, duration];
      return;
    }
    durationRef.current[key] = [Date.now(), null, null];
  };

  /**
   * Takes the current timestamp and subtracts the starting timestamp.
   * @returns duration in ms
   */
  const pauseTimer = (key: string = 'DEFAULT'): number | null => {
    // check if timer is there for given key
    if (durationRef.current[key]?.length === 3) {
      const [startTime] = durationRef.current[key];
      let [endTime, duration] = durationRef.current[key];
      endTime = Date.now();
      duration = endTime - (startTime ?? 0);
      durationRef.current[key] = [startTime, endTime, duration];
      return isNaN(duration) ? null : duration;
    }
    return null;
  };

  /**
   * Resets the timer.
   */
  const clearTimer = (key: string = 'DEFAULT', clearAll: boolean = false) => {
    if (clearAll) {
      durationRef.current = {};
    } else {
      delete durationRef.current[key];
    }
  };

  return {
    startTimer,
    pauseTimer,
    clearTimer,
    durationRef,
  };
};

export default useDuration;
